'use client';

/**
 * useThemeMode Hook
 *
 * Manages theme mode (light/dark) with persistence and system preference detection.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ThemeMode } from '../theme';

// useLayoutEffect on client (syncs theme before paint), useEffect on server (avoids SSR warnings)
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// ============================================================================
// Types
// ============================================================================

export interface UseThemeModeOptions {
  /**
   * Storage key for persisting theme preference
   * @default 'theme-mode'
   */
  storageKey?: string;

  /**
   * Default theme mode if no preference is stored
   * @default 'system'
   */
  defaultMode?: ThemeMode | 'system';

  /**
   * Callback when theme changes
   */
  onThemeChange?: (mode: ThemeMode) => void;
}

export interface UseThemeModeReturn {
  /**
   * Current theme mode
   */
  mode: ThemeMode;

  /**
   * Set theme mode
   */
  setMode: (mode: ThemeMode | 'system') => void;

  /**
   * Toggle between light and dark
   */
  toggle: () => void;

  /**
   * Whether the current mode is from system preference
   */
  isSystemMode: boolean;

  /**
   * System's preferred color scheme
   */
  systemPreference: ThemeMode;
}

// ============================================================================
// Hook
// ============================================================================

export function useThemeMode(options: UseThemeModeOptions = {}): UseThemeModeReturn {
  const { storageKey = 'theme-mode', defaultMode = 'system', onThemeChange } = options;

  // Get system preference
  const getSystemPreference = useCallback((): ThemeMode => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, []);

  // Get stored preference
  const getStoredPreference = useCallback((): ThemeMode | 'system' | null => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(storageKey);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    return null;
  }, [storageKey]);

  // Get initial mode
  const getInitialMode = useCallback((): {
    mode: ThemeMode;
    isSystem: boolean;
  } => {
    const stored = getStoredPreference();
    if (stored === 'system' || (stored === null && defaultMode === 'system')) {
      return { mode: getSystemPreference(), isSystem: true };
    }
    if (stored === 'light' || stored === 'dark') {
      return { mode: stored, isSystem: false };
    }
    return {
      mode: defaultMode === 'system' ? getSystemPreference() : defaultMode,
      isSystem: defaultMode === 'system',
    };
  }, [defaultMode, getStoredPreference, getSystemPreference]);

  // Always initialize with 'light' to match server-rendered HTML.
  // The real preference is synced in useLayoutEffect before the first paint,
  // preventing both hydration mismatches and visible theme flashes.
  const [mode, setModeState] = useState<ThemeMode>('light');
  const [isSystemMode, setIsSystemMode] = useState(defaultMode === 'system');
  const [systemPreference, setSystemPreference] = useState<ThemeMode>('light');

  // Sync to stored/system preference before the first browser paint
  const hasSynced = useRef(false);
  useIsomorphicLayoutEffect(() => {
    if (hasSynced.current) return;
    hasSynced.current = true;
    const actual = getInitialMode();
    setModeState(actual.mode);
    setIsSystemMode(actual.isSystem);
    setSystemPreference(getSystemPreference());
  }, [getInitialMode, getSystemPreference]);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const newPreference = e.matches ? 'dark' : 'light';
      setSystemPreference(newPreference);

      // Update mode if following system preference
      if (isSystemMode) {
        setModeState(newPreference);
        onThemeChange?.(newPreference);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isSystemMode, onThemeChange]);

  // Set mode
  const setMode = useCallback(
    (newMode: ThemeMode | 'system') => {
      if (typeof window === 'undefined') return;

      localStorage.setItem(storageKey, newMode);

      if (newMode === 'system') {
        const systemMode = getSystemPreference();
        setModeState(systemMode);
        setIsSystemMode(true);
        onThemeChange?.(systemMode);
      } else {
        setModeState(newMode);
        setIsSystemMode(false);
        onThemeChange?.(newMode);
      }
    },
    [storageKey, getSystemPreference, onThemeChange],
  );

  // Toggle mode
  const toggle = useCallback(() => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
  }, [mode, setMode]);

  return useMemo(
    () => ({
      mode,
      setMode,
      toggle,
      isSystemMode,
      systemPreference,
    }),
    [mode, setMode, toggle, isSystemMode, systemPreference],
  );
}

export default useThemeMode;
